<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import UazapiAPI from 'dashboard/api/uazapi';

const STATUS_LABELS = {
  CONNECTED: 'CONNECTED',
  CONNECTING: 'CONNECTING',
  DISCONNECTED: 'DISCONNECTED',
  FAILED: 'FAILED',
};

export default {
  components: {
    SettingsSection,
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isActionLoading: false,
      isReconfiguring: false,
      instanceStatus: null,
      qrCode: null,
      phoneNumber: null,
      instanceUrl: null,
      chatwootWebhookUrl: null,
      error: null,
      statusPollInterval: null,
      showPairingCode: false,
      pairingPhoneNumber: '',
      pairingCode: '',
      pairingError: '',
      isLoadingPairingCode: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    inboxId() {
      return this.inbox.id;
    },
    normalizedStatus() {
      return (this.instanceStatus || '').toUpperCase();
    },
    statusClass() {
      const statusMap = {
        CONNECTED:
          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        CONNECTING:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        DISCONNECTED:
          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      };
      return statusMap[this.normalizedStatus] || statusMap.DISCONNECTED;
    },
    statusLabel() {
      const status = this.normalizedStatus || 'DISCONNECTED';
      const key = `INBOX_MGMT.UAZAPI.STATUS.${status}`;
      return this.$t(key);
    },
    showQrCode() {
      return this.normalizedStatus === STATUS_LABELS.CONNECTING && this.qrCode;
    },
    isConnected() {
      return this.normalizedStatus === STATUS_LABELS.CONNECTED;
    },
    isDisconnected() {
      return this.normalizedStatus === STATUS_LABELS.DISCONNECTED;
    },
    isConnecting() {
      return this.normalizedStatus === STATUS_LABELS.CONNECTING;
    },
    canConnect() {
      return (
        this.isDisconnected || this.normalizedStatus === STATUS_LABELS.FAILED
      );
    },
    canDisconnect() {
      return this.isConnected || this.isConnecting;
    },
    isPolling() {
      return this.isConnecting;
    },
  },
  mounted() {
    this.fetchStatus();
  },
  beforeUnmount() {
    this.clearStatusPollInterval();
  },
  methods: {
    async fetchStatus(silent = false) {
      if (!silent) this.isLoading = true;
      if (!silent) this.error = null;
      try {
        const response = await UazapiAPI.getStatus(
          this.accountId,
          this.inboxId
        );
        this.updateStatusFromResponse(response.data);
        this.setupPollingIfNeeded();
      } catch (err) {
        if (!silent)
          this.error =
            err.message || this.$t('INBOX_MGMT.UAZAPI.STATUS.FAILED');
      } finally {
        if (!silent) this.isLoading = false;
      }
    },
    updateStatusFromResponse(data) {
      this.instanceStatus = data.status;
      this.qrCode = data.qr_code;
      this.phoneNumber = data.phone_number;
      this.instanceUrl = data.instance_url;
      if (data.chatwoot_webhook_url)
        this.chatwootWebhookUrl = data.chatwoot_webhook_url;
    },
    async reconfigureChatwoot() {
      this.isReconfiguring = true;
      try {
        const response = await UazapiAPI.reconfigureChatwoot(
          this.accountId,
          this.inboxId
        );
        if (response.data?.webhook_url)
          this.chatwootWebhookUrl = response.data.webhook_url;
        useAlert(this.$t('INBOX_MGMT.UAZAPI.ALERTS.CHATWOOT_CONFIG_SUCCESS'));
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.UAZAPI.ALERTS.ERROR'));
      } finally {
        this.isReconfiguring = false;
      }
    },
    setupPollingIfNeeded() {
      this.clearStatusPollInterval();
      if (this.isPolling) {
        this.statusPollInterval = setInterval(() => {
          this.fetchStatus(true);
        }, 5000);
      }
    },
    clearStatusPollInterval() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval);
        this.statusPollInterval = null;
      }
    },
    async connectInstance() {
      this.isActionLoading = true;
      try {
        const response = await UazapiAPI.connect(this.accountId, this.inboxId);
        // connect endpoint now returns status+QR code after a brief wait
        if (response.data) {
          this.updateStatusFromResponse(response.data);
        } else {
          await this.fetchStatus();
        }
        useAlert(this.$t('INBOX_MGMT.UAZAPI.ALERTS.CONNECT_SUCCESS'));
        this.setupPollingIfNeeded();
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.UAZAPI.ALERTS.ERROR'));
      } finally {
        this.isActionLoading = false;
      }
    },
    async reconnectInstance() {
      this.isActionLoading = true;
      try {
        await UazapiAPI.reconnect(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.UAZAPI.ALERTS.RESTART_SUCCESS'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.UAZAPI.ALERTS.ERROR'));
      } finally {
        this.isActionLoading = false;
      }
    },
    async disconnectInstance() {
      if (!window.confirm(this.$t('INBOX_MGMT.UAZAPI.CONFIRM_DISCONNECT'))) {
        return;
      }
      this.isActionLoading = true;
      try {
        await UazapiAPI.disconnect(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.UAZAPI.ALERTS.DISCONNECT_SUCCESS'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.UAZAPI.ALERTS.ERROR'));
      } finally {
        this.isActionLoading = false;
      }
    },
    async requestPairingCode() {
      if (!this.pairingPhoneNumber) return;
      this.isLoadingPairingCode = true;
      this.pairingError = '';
      this.pairingCode = '';
      try {
        const { data } = await UazapiAPI.requestPairingCode(
          this.accountId,
          this.inboxId,
          this.pairingPhoneNumber
        );
        this.pairingCode = data.code;
        useAlert(this.$t('INBOX_MGMT.UAZAPI.PAIRING_CODE.SUCCESS'));
      } catch {
        this.pairingError = this.$t('INBOX_MGMT.UAZAPI.PAIRING_CODE.ERROR');
      } finally {
        this.isLoadingPairingCode = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner size="" />
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4"
    >
      <p class="text-sm text-red-800 dark:text-red-200 mb-2">{{ error }}</p>
      <NextButton size="sm" @click="fetchStatus">
        {{ $t('INBOX_MGMT.WAHA.RETRY') }}
      </NextButton>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Instance Status -->
      <SettingsSection
        :title="$t('INBOX_MGMT.UAZAPI.STATUS.TITLE')"
        :sub-title="$t('INBOX_MGMT.UAZAPI.STATUS.SUBTITLE')"
      >
        <div class="flex items-center gap-4 mb-4">
          <span
            class="px-3 py-1 rounded-full text-sm font-medium"
            :class="statusClass"
          >
            {{ statusLabel }}
          </span>
          <NextButton
            size="sm"
            variant="outline"
            :is-loading="isActionLoading"
            @click="fetchStatus"
          >
            {{ $t('INBOX_MGMT.WAHA.REFRESH_STATUS') }}
          </NextButton>
        </div>

        <div v-if="phoneNumber" class="text-sm text-n-slate-11 mb-2">
          <span class="font-medium"
            >{{ $t('INBOX_MGMT.UAZAPI.INFO.PHONE_NUMBER') }}:</span
          >
          {{ phoneNumber }}
        </div>

        <div v-if="instanceUrl" class="text-sm text-n-slate-11">
          <span class="font-medium"
            >{{ $t('INBOX_MGMT.UAZAPI.INFO.INSTANCE_URL') }}:</span
          >
          {{ instanceUrl }}
        </div>

        <div
          v-if="!phoneNumber && !instanceUrl"
          class="text-sm text-n-slate-10 italic"
        >
          {{ $t('INBOX_MGMT.UAZAPI.INFO.NOT_CONNECTED') }}
        </div>
      </SettingsSection>

      <!-- Chatwoot Integration Section -->
      <SettingsSection
        :title="$t('INBOX_MGMT.UAZAPI.CHATWOOT_INTEGRATION.TITLE')"
        :sub-title="$t('INBOX_MGMT.UAZAPI.CHATWOOT_INTEGRATION.SUBTITLE')"
      >
        <div v-if="chatwootWebhookUrl" class="text-sm text-n-slate-11 mb-3">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.UAZAPI.CHATWOOT_INTEGRATION.WEBHOOK_URL_LABEL') }}
          </span>
          <code
            class="ml-1 text-xs bg-n-alpha-1 px-2 py-0.5 rounded border border-n-weak break-all"
          >
            {{ chatwootWebhookUrl }}
          </code>
        </div>
        <div v-else class="text-sm text-yellow-600 dark:text-yellow-400 mb-3">
          {{ $t('INBOX_MGMT.UAZAPI.CHATWOOT_INTEGRATION.NOT_CONFIGURED') }}
        </div>
        <NextButton
          size="sm"
          variant="outline"
          :is-loading="isReconfiguring"
          @click="reconfigureChatwoot"
        >
          {{ $t('INBOX_MGMT.UAZAPI.CHATWOOT_INTEGRATION.RECONFIGURE') }}
        </NextButton>
      </SettingsSection>

      <!-- QR Code Section -->
      <SettingsSection
        v-if="showQrCode"
        :title="$t('INBOX_MGMT.UAZAPI.QR_CODE.TITLE')"
        :sub-title="$t('INBOX_MGMT.UAZAPI.QR_CODE.DESCRIPTION')"
      >
        <div class="flex flex-col items-center gap-4">
          <div class="bg-white p-4 rounded-lg shadow-sm">
            <img
              :src="qrCode"
              alt="WhatsApp QR Code"
              class="w-64 h-64 object-contain"
            />
          </div>
          <p class="text-sm text-n-slate-11 text-center max-w-md">
            {{ $t('INBOX_MGMT.UAZAPI.QR_CODE.DESCRIPTION') }}
          </p>
          <NextButton
            size="sm"
            variant="outline"
            :is-loading="isActionLoading"
            @click="fetchStatus"
          >
            {{ $t('INBOX_MGMT.UAZAPI.QR_CODE.REFRESH') }}
          </NextButton>

          <!-- Pairing Code Toggle -->
          <button
            class="mt-2 text-sm text-woot-500 hover:text-woot-600 underline cursor-pointer bg-transparent border-0"
            @click="showPairingCode = !showPairingCode"
          >
            {{
              showPairingCode
                ? $t('INBOX_MGMT.WAHA.PAIRING_CODE.TOGGLE_HIDE')
                : $t('INBOX_MGMT.WAHA.PAIRING_CODE.TOGGLE_SHOW')
            }}
          </button>

          <!-- Pairing Code Form -->
          <div v-if="showPairingCode" class="w-full max-w-sm space-y-3 mt-1">
            <p class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.UAZAPI.PAIRING_CODE.DESCRIPTION') }}
            </p>
            <input
              v-model="pairingPhoneNumber"
              type="tel"
              :placeholder="
                $t('INBOX_MGMT.UAZAPI.PAIRING_CODE.PHONE_PLACEHOLDER')
              "
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-1 text-n-slate-12"
            />
            <button
              :disabled="isLoadingPairingCode || !pairingPhoneNumber"
              class="w-full px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="requestPairingCode"
            >
              {{
                isLoadingPairingCode
                  ? $t('INBOX_MGMT.WAHA.PAIRING_CODE.GETTING_CODE')
                  : $t('INBOX_MGMT.UAZAPI.PAIRING_CODE.REQUEST_BUTTON')
              }}
            </button>

            <div
              v-if="pairingCode"
              class="p-4 bg-n-alpha-1 rounded-lg text-center border border-n-weak"
            >
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INBOX_MGMT.WAHA.PAIRING_CODE.RESULT_INSTRUCTIONS') }}
              </p>
              <p
                class="text-2xl font-mono font-bold text-n-slate-12 tracking-widest"
              >
                {{ pairingCode }}
              </p>
              <p class="text-xs text-n-slate-10 mt-2">
                {{ $t('INBOX_MGMT.WAHA.PAIRING_CODE.RESULT_HINT') }}
              </p>
            </div>

            <p v-if="pairingError" class="text-sm text-red-500">
              {{ pairingError }}
            </p>
          </div>
        </div>
      </SettingsSection>

      <!-- Instance Controls -->
      <SettingsSection
        :title="$t('INBOX_MGMT.UAZAPI.CONTROLS.TITLE')"
        :sub-title="$t('INBOX_MGMT.UAZAPI.CONTROLS.SUBTITLE')"
      >
        <div class="flex flex-wrap gap-3">
          <NextButton
            v-if="canConnect"
            :is-loading="isActionLoading"
            @click="connectInstance"
          >
            {{ $t('INBOX_MGMT.UAZAPI.CONTROLS.CONNECT') }}
          </NextButton>

          <NextButton
            v-if="isConnected"
            variant="outline"
            :is-loading="isActionLoading"
            @click="reconnectInstance"
          >
            {{ $t('INBOX_MGMT.UAZAPI.CONTROLS.RESTART') }}
          </NextButton>

          <NextButton
            v-if="canDisconnect"
            variant="outline"
            color="ruby"
            :is-loading="isActionLoading"
            @click="disconnectInstance"
          >
            {{ $t('INBOX_MGMT.UAZAPI.CONTROLS.DISCONNECT') }}
          </NextButton>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
